import {
	Alert,
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	styled,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableRow,
	Typography,
} from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUsersList } from 'redux/reducers/user/selectors';
import { StatusMenu } from 'ui/StatusMenu';
import { UserAvatarMenu } from 'ui/UserAvatarMenu';
import { LaunchCorrectionScriptDialog } from 'components/ErrorsCorrections/ErrorItem/ErrorCorrection/LaunchCorrectionScript/LaunchCorrectionScriptDialog';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import { EditedTextParameter } from 'components/ErrorsCorrections/ErrorItem/ErrorCorrection/EdittedTextParameter';
import {
	getErrorCorrection,
	getErrorCorrectionsList,
	getScripts,
	getStartScriptError,
	getStartScriptResponse,
	getStartScriptResponseArray,
	getUpdateParameterCounter,
} from 'redux/reducers/errorCorrections/selectors';
import { useDebounce } from 'use-debounce';
import {
	resetParameterUpdateCounter,
	startScriptRequest,
	startScriptResponseArrayReset,
	updateCommentRequest,
	updateCommunicationWithClientRequest,
	updateProposedSolutionRequest,
	updateResponsibleUserRequest,
	updateStatusRequest,
} from 'redux/reducers/errorCorrections/reducer';
import { ScriptsTable } from 'components/ErrorsCorrections/ErrorItem/ErrorCorrection/ScriptsTable';
import { useLocation } from 'react-router';
import queryString from 'query-string';
import { LaunchCorrectionScript } from 'components/ErrorsCorrections/ErrorItem/ErrorCorrection/LaunchCorrectionScript';
import { IErrorCorrectionsScriptParameterValue } from 'components/ErrorsCorrections/ErrorItem/ErrorCorrection/LaunchCorrectionScript/types';
import { TErrorCorrectionsStartScriptResponseData } from 'redux/reducers/errorCorrections/types';
import { nanoid } from '@reduxjs/toolkit';
import { ErrorCorrectionsScriptResult } from 'redux/reducers/errorCorrections/constants';
import { COLORS } from 'services/constants/colors';
import { ErrorsStatus } from '../constants';

export type TActionPanelProps = {
	errorIds: number[];
	checkedRows: boolean[];
};

export const ActionPanel: FC<TActionPanelProps> = ({ errorIds, checkedRows }) => {
	const { search } = useLocation();
	const location = useLocation();
	const navigateParams = queryString.parse(search);
	const numberOfErrors = errorIds?.length ?? 0;
	const dispatch = useDispatch();
	const errors = useSelector(getErrorCorrectionsList);
	const usersList = useSelector(getUsersList);
	const updateParameterCounter = useSelector(getUpdateParameterCounter);
	const [openScreensaver, setOpenScreensaver] = useState(false);
	const [launchCorrectionScriptsDisable, setLaunchCorrectionScriptsDisable] = useState(true);
	const startScriptResponseArray = useSelector(getStartScriptResponseArray);

	useEffect(() => {
		let sameEm = false;
		let sameTypeOfError = false;
		if ((errorIds.length ?? 0) > 0) {
			const { emName } = errors!.data[checkedRows.findIndex(row => row)];
			const { typeOfError } = errors!.data[checkedRows.findIndex(row => row)];
			const checkedErrors = errors!.data.filter((error, index) => checkedRows[index]);
			sameEm = checkedErrors.reduce((acc, error) => acc && error?.emName === emName, true);
			sameTypeOfError = checkedErrors.reduce(
				(acc, error) => acc && error?.typeOfError === typeOfError,
				true,
			);
		}
		setLaunchCorrectionScriptsDisable(!sameEm || !sameTypeOfError);
		// console.log('sameEm', sameEm);
	}, [checkedRows, errorIds, errors]);

	const [userIndex, setUserIndex] = useState<number | null>(null);
	const handleUserSelect = (index: number) => {
		if (!usersList) return;
		setUserIndex(index);
	};
	const [status, setStatus] = useState('');
	const handleSetStatus = (s: ErrorsStatus) => {
		setStatus(s);
	};
	const [countOfParameters, setCountOfParameters] = useState(0);

	const [proposedSolutionText, setProposedSolutionText] = useState<string>('');

	const [communicationWithTheClient, setCommunicationWithTheClient] = useState<string>('');

	const [comments, setComments] = useState<string>('');

	const [openScriptDialog, setOpenScriptDialog] = useState(false);
	const handleOpenScriptDialog = () => {
		setOpenScriptDialog(true);
	};
	const handleCloseScriptDialog = () => {
		setOpenScriptDialog(false);
	};

	// useEffect(() => {
	// 	if (!error) return;
	// 	setProposedSolutionText(error?.proposedSolution ?? '');
	// 	setCommunicationWithTheClient(error?.communicationWithClient ?? '');
	// 	setComments(error?.comment ?? '');
	// }, [error]);

	useEffect(() => {
		dispatch(resetParameterUpdateCounter());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const scripts = useSelector(getScripts);

	const [activeStep, setActiveStep] = useState(0);
	const [submitted, setSubmitted] = useState(false);
	const [selectedScript, setSelectedScript] = useState<number | null>(null);
	const [errorParams, setErrorParams] = useState<IErrorCorrectionsScriptParameterValue[]>(
		scripts?.[selectedScript ?? 0]?.parameters.map(param => {
			return {
				fieldName: param.fieldName,
				name: param.name,
				value: '',
				type: param.type,
			};
		}) ?? [],
	);

	const handleEraseForm = () => {
		setSelectedScript(null);
		setErrorParams([]);
		setActiveStep(0);
	};

	const handleSubmitCorrectionScript = () => {
		setOpenScriptDialog(false);
	};

	const [startedScripts, setStartedScripts] = useState(false);

	const startScriptResponse = useSelector(getStartScriptResponse);
	// const [startScriptResponseArray, setStartScriptResponseArray] = useState<
	// 	TErrorCorrectionsStartScriptResponseData[]
	// >([]);

	const handleStart = () => {
		// setStartScriptResponseArray([]);
		dispatch(startScriptResponseArrayReset());
		let numberOfParameters = 0;
		if (userIndex !== null) numberOfParameters += numberOfErrors;
		if (status) numberOfParameters += numberOfErrors;
		if (proposedSolutionText) numberOfParameters += numberOfErrors;
		if (communicationWithTheClient) numberOfParameters += numberOfErrors;
		if (comments) numberOfParameters += numberOfErrors;
		if (selectedScript !== null) numberOfParameters += numberOfErrors;
		dispatch(resetParameterUpdateCounter());
		setCountOfParameters(numberOfParameters);
		// console.log('numberOfParameters', numberOfParameters);
		setOpenScreensaver(true);

		if (userIndex !== null) {
			errorIds.forEach((errorId, index) => {
				dispatch(
					updateResponsibleUserRequest({
						errorId: +(errorId ?? 0),
						responsibleUserId: usersList?.[userIndex].id ?? '',
						reload: false,
					}),
				);
			});
		}
		if (status) {
			errorIds.forEach((errorId, index) => {
				dispatch(
					updateStatusRequest({
						text: status,
						errorId: +(errorId ?? 0),
						reload: false,
					}),
				);
			});
		}
		if (proposedSolutionText) {
			errorIds.forEach(errorId => {
				dispatch(
					updateProposedSolutionRequest({
						text: proposedSolutionText,
						errorId: +(errorId ?? 0),
					}),
				);
			});
		}
		if (communicationWithTheClient) {
			errorIds.forEach(errorId => {
				dispatch(
					updateCommunicationWithClientRequest({
						text: communicationWithTheClient,
						errorId: +(errorId ?? 0),
					}),
				);
			});
		}
		if (comments) {
			errorIds.forEach(errorId => {
				dispatch(
					updateCommentRequest({
						text: comments,
						errorId: +(errorId ?? 0),
					}),
				);
			});
		}
		if (selectedScript !== null && scripts) {
			setStartedScripts(true);
			errorIds.forEach(errorId => {
				dispatch(
					startScriptRequest({
						errorId,
						scriptId: scripts[selectedScript].id,
						parameters:
							// {},
							errorParams.reduce<{ [key: string]: any }>((acc, item) => {
								acc[item.fieldName] = String(item.value);
								return acc;
							}, {}),
					}),
				);
			});
		}
		// window.location.reload();
	};
	const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
		height: 10,
		borderRadius: 5,
		[`&.${linearProgressClasses.colorPrimary}`]: {
			backgroundColor: theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800],
		},
		[`& .${linearProgressClasses.bar}`]: {
			borderRadius: 5,
			backgroundColor: COLORS.PRIMARY,
		},
	}));

	// useEffect(
	// 	() => {
	// 		// if (!startScriptResponse || !startedScripts) return;
	// 		const startScriptResponseArrayTmp = [...startScriptResponseArray];
	// 		startScriptResponseArrayTmp.push(startScriptResponse);
	// 		setStartScriptResponseArray(startScriptResponseArrayTmp);
	// 		console.log('-------------------');
	// 		console.log('useEffect startScriptResponse', startScriptResponse);
	// 		// if (updateParameterCounter === countOfParameters) setStartedScripts(false);
	// 	},
	// 	// eslint-disable-next-line react-hooks/exhaustive-deps
	// 	[startScriptResponse],
	// );
	console.log('startScriptResponse', startScriptResponse);
	console.log('startScriptResponseArray', startScriptResponseArray);
	console.log('updateParameterCounter', updateParameterCounter);
	console.log('selectedScript', selectedScript);

	useEffect(() => {
		if (!updateParameterCounter) return;
		if (updateParameterCounter === countOfParameters) {
			if (selectedScript !== null) {
				// setStartedScripts(false);
				return;
			}
			window.location.reload();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [updateParameterCounter]);

	return (
		<>
			<Box
				style={{
					padding: '5px',
					// color: '#015079',
					// fontWeight: 'bold',
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'space-between',
					gap: '5px',
				}}
			>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: '5px',
						border: `1px solid ${COLORS.BORDER}`,
						borderRadius: '12px',
						padding: '5px',
					}}
				>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'row',
							gap: '5px',
							justifyContent: 'space-between',
						}}
					>
						<UserAvatarMenu
							user={userIndex === null ? null : (usersList?.[userIndex] ?? null)}
							users={usersList ?? []}
							size="xs"
							onClick={handleUserSelect}
						/>
						<StatusMenu status={status as ErrorsStatus} onClick={handleSetStatus} size="xs" />
					</Box>
					<Button
						// disabled={!navigateParams?.search || !errorIds?.length || !navigateParams?.typeOfError}
						disabled={launchCorrectionScriptsDisable}
						variant="contained"
						color="primary"
						size="small"
						sx={{
							textTransform: 'none',
							width: '100%',
							whiteSpace: 'nowrap',
							height: 'fit-content',
						}}
						onClick={handleOpenScriptDialog}
					>
						{selectedScript !== null ? scripts?.[selectedScript]?.name : 'Select Correction Script'}
					</Button>
				</Box>

				<Box sx={{ display: 'flex', flexDirection: 'row', gap: '5px', width: '100%' }}>
					<EditedTextParameter
						name="Proposed solution"
						value={proposedSolutionText}
						onChange={setProposedSolutionText}
					/>
					<EditedTextParameter
						name="Communication with the client"
						value={communicationWithTheClient}
						onChange={setCommunicationWithTheClient}
					/>
					<EditedTextParameter name="Comments" value={comments} onChange={setComments} />
				</Box>
				<Box
					sx={{
						width: '75px!important',
						height: '75px',
					}}
				>
					<Button
						variant="contained"
						color="primary"
						sx={{
							textTransform: 'none',
							width: '75px!important',
							height: '75px',
						}}
						onClick={handleStart}
						disabled={
							userIndex === null &&
							!status &&
							!proposedSolutionText &&
							!communicationWithTheClient &&
							!comments &&
							selectedScript === null
						}
					>
						Start
					</Button>
				</Box>
			</Box>
			<LaunchCorrectionScriptDialog
				open={openScriptDialog}
				setClose={handleCloseScriptDialog}
				handleSubmit={handleSubmitCorrectionScript}
				setErrorParams={setErrorParams}
				setSelectedScript={setSelectedScript}
				setActiveStep={setActiveStep}
				activeStep={activeStep}
				errorParams={errorParams}
				selectedScript={selectedScript}
				setSubmitted={setSubmitted}
				submitted={submitted}
				isGroupScripts
			/>

			<Dialog
				open={openScreensaver}
				sx={{
					backdropFilter: 'blur(5px)',
					backgroundColor: `${COLORS.PRIMARY}89`,
					// with: '100%',
					// maxWidth: '100%',

					'& .MuiDialog-paper': {
						backgroundColor: 'transparent',
						boxShadow: 'none',
						// with: '900px',
						// maxWidth: '1000px',
						// m: 0,
						// p: 0,
					},
					'& .MuiPaper-root': {
						maxWidth: '1000px',
						// with: '900px',
						// with: '100%',
						// maxWidth: '100%',
					},
				}}
			>
			<DialogTitle
				variant="h4"
				id="alert-dialog-title"
				sx={{
					color: COLORS.font_light,
					justifyContent: 'center',
					display: 'flex',
					alignItems: 'center',
				}}
				>
					Parameters are set
				</DialogTitle>
				<DialogContent
					sx={{
						// padding: 15,
						justifyContent: 'center',
						display: 'flex',
						alignItems: 'center',
						flexDirection: 'column',
						// m: 0,
						// p: 0,
						// width: '100%',
						// width: '100vw',
						// maxWidth: '1000px',
					}}
				>
					<BorderLinearProgress
						variant="determinate"
						value={countOfParameters ? (updateParameterCounter / countOfParameters) * 100 : 0}
						sx={{
							borderRadius: '50px',
							width: '800px',
							height: '16px',
							backgroundColor: COLORS.PAPER,
							mb: '40px',
						}}
					/>
					{selectedScript !== null && startScriptResponseArray.length > 0 && (
						<TableContainer
							sx={{
								backgroundColor: `${COLORS.PAPER}10`,
								p: 2,
								borderRadius: '12px!important',
								width: '800px',
								// maxWidth: '800px',
								m: 0,
							}}
						>
							<Typography
								sx={{
									color: COLORS.font_light,
									fontWeight: 'bold',
									width: '800px',
									mb: 2,
								}}
							>
								Script execution results
							</Typography>
							<Table>
								<TableBody>
									{startScriptResponseArray.map((error, index) => {
										// const checked = checkedRows[index];
										// if (!checked) return null;
										// responseIndex += 1;
										return (
											<TableRow key={nanoid()}>
											<TableCell
												sx={{
													color: COLORS.font_light,
													p: 1,
													verticalAlign: 'top',
												}}
											>
												{error?.easOrderId}
											</TableCell>
											<TableCell
												sx={{
													color: COLORS.font_light,
													p: 1,
													verticalAlign: 'top',
												}}
											>
													{error?.externalOrderId}
												</TableCell>
												{/* <TableCell sx={{ p: 1, m: 0, verticalAlign: 'top' }}>
													<Chip
														label={error?.errorneusOrderStatus}
														size="small"
														sx={{
															bgcolor:
																error?.errorneusOrderStatus === ErrorsStatus.FIXED
																	? 'success.light'
																	: error?.errorneusOrderStatus === ErrorsStatus.NEW
																	? 'warning.light'
																	: error?.errorneusOrderStatus === ErrorsStatus.CONTACTING_CLIENT
																	? 'primary.light'
																	: error?.errorneusOrderStatus === ErrorsStatus.WORKING_ON_IT
																	? 'info.light'
																	: '#c5c5c5',
															color: '#000000c0',
														}}
													/>
												</TableCell> */}
												<TableCell
													align="center"
													sx={{
														p: 1,
														m: 0,
														verticalAlign: 'top',
														fontWeight: 'bold',
														color:
															error?.result === ErrorCorrectionsScriptResult.Failed
																? '#f86363'
																: error?.result === ErrorCorrectionsScriptResult.Done
																	? 'success.main'
																	: 'warning.main',
													}}
												>
													{error?.result}
												</TableCell>
												<TableCell
													sx={{
														p: 1,
														m: 0,
														color: COLORS.font_light,
														verticalAlign: 'top',
														fontSize: '0.8rem',
													}}
												>
													{error?.message}
												</TableCell>
											</TableRow>
										);
									})}
								</TableBody>
							</Table>
						</TableContainer>
					)}{' '}
					{/* <CircularProgressWithLabel value={progress} /> */}
					{/* <div
						style={{
							position: 'absolute',
							top: '50%',
							left: '50%',
							transform: 'translate(-50%, -50%)',
							fontSize: '60px',
							fontWeight: 'bold',
							color: SCREENSAVER_TIMEOUT - progress < 10 ? `#ff7fa4` : '#fff',
						}}
					>
						{SCREENSAVER_TIMEOUT - progress}
					</div> */}
				</DialogContent>
				<DialogActions
					sx={{
						display: 'flex',
						flexDirection: 'row',
						justifyContent: 'center',
						// width: '100%',
					}}
				>
					{selectedScript !== null && updateParameterCounter === countOfParameters && (
						<Button
							variant="contained"
							onClick={() => {
								setOpenScreensaver(false);
								window.location.reload();
							}}
							color="primary"
							autoFocus
						>
							Close
						</Button>
					)}
				</DialogActions>
			</Dialog>
		</>
	);
};
